import React, { useState, useEffect, useCallback } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import axios from 'axios';

/**
 * 顧客検索用オートコンプリートコンポーネント（1文字対応版）
 * 
 * 🚨 パフォーマンス最適化済み：
 * - 1文字検索時はより厳格な制限（先頭一致のみ）
 * - デバウンス時間を動的調整
 * - キャッシュ機能追加
 * - エラーハンドリング強化
 */
const ClientAutocomplete = ({ 
  selectedClient, 
  onClientChange, 
  label = "顧客を選択",
  placeholder = "顧客名で検索...",
  required = false,
  disabled = false,
  size = "medium",
  sx = {},
  // 新しいオプション
  minSearchLength = 2, // 最小検索文字数（デフォルト2文字）
  maxResults = 20,     // 最大結果数
  enableCache = true   // キャッシュ有効化
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchCache, setSearchCache] = useState(new Map());
  const [error, setError] = useState('');

  // 🔹 検索処理（最適化版）
  const performSearch = useCallback(async (searchQuery) => {
    if (searchQuery.length < minSearchLength) {
      setOptions([]);
      return;
    }

    // キャッシュチェック
    if (enableCache && searchCache.has(searchQuery)) {
      const cachedResult = searchCache.get(searchQuery);
      setOptions(cachedResult);
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        throw new Error('認証トークンが見つかりません');
      }

      // 🔹 1文字検索時は結果数を制限し、先頭一致を優先
      const limit = searchQuery.length === 1 ? Math.min(maxResults, 10) : maxResults;
      const searchType = searchQuery.length === 1 ? 'prefix' : 'contains';
      
      const response = await axios.get(
        `/api/clients/autocomplete`, {
          params: {
            q: searchQuery,
            limit: limit,
            searchType: searchType // バックエンド側で実装が必要
          },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000 // タイムアウト設定
        }
      );
      
      const results = response.data || [];
      
      // キャッシュに保存（最大100件まで）
      if (enableCache) {
        if (searchCache.size >= 100) {
          // 古いキャッシュを削除
          const firstKey = searchCache.keys().next().value;
          searchCache.delete(firstKey);
        }
        setSearchCache(new Map(searchCache.set(searchQuery, results)));
      }
      
      setOptions(results);
      
    } catch (error) {
      
      if (error.code === 'ECONNABORTED') {
        setError('検索がタイムアウトしました');
      } else if (error.response?.status === 429) {
        setError('検索回数が上限に達しました。しばらく待ってから再試行してください');
      } else {
        setError('検索中にエラーが発生しました');
      }
      
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [minSearchLength, maxResults, enableCache, searchCache]);

  // 🔹 デバウンス付き検索実行
  useEffect(() => {
    if (!open) {
      setOptions([]);
      return;
    }

    // 動的デバウンス：1文字は500ms、2文字以上は300ms
    const debounceTime = inputValue.length === 1 ? 500 : 300;
    
    const timeoutId = setTimeout(() => {
      performSearch(inputValue);
    }, debounceTime);

    return () => clearTimeout(timeoutId);
  }, [inputValue, open, performSearch]);

  // 🔹 顧客選択処理（エラーハンドリング強化）
  const handleChange = async (event, newValue) => {
    if (newValue && typeof newValue === 'object' && newValue.id) {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error('認証トークンが見つかりません');
        }

        const response = await axios.get(
          `/api/clients/${newValue.id}/basic`,
          { 
            headers: { Authorization: `Bearer ${token}` },
            timeout: 3000
          }
        );
        
        onClientChange(response.data);
        setError(''); // エラーをクリア
        
      } catch (error) {
        
        // 基本情報のみで続行
        onClientChange(newValue);
        
        if (error.response?.status === 404) {
          setError('選択された顧客の詳細情報が見つかりません');
        } else {
          setError('顧客詳細の取得中にエラーが発生しました');
        }
      }
    } else {
      onClientChange(null);
      setError('');
    }
  };

  // 🔹 入力値変更処理
  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    
    // エラーをクリア
    if (error) {
      setError('');
    }
  };

  // 🔹 ヘルパーテキストの生成
  const getHelperText = () => {
    if (error) return error;
    
    if (inputValue.length > 0 && inputValue.length < 2) {
      return "2文字以上入力してください";
    }
    
    if (inputValue.length === 2) {
      return "2文字検索は結果が制限されます";
    }
    
    return "";
  };

  // 🔹 noOptionsText の生成
  const getNoOptionsText = () => {
    if (inputValue.length < 2) {
      return "2文字以上入力してください";
    }
    
    if (loading) {
      return "検索中...";
    }
    
    if (error) {
      return "検索でエラーが発生しました";
    }
    
    return "該当する顧客が見つかりません";
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setError(''); // ドロップダウンを閉じる時にエラーをクリア
      }}
      value={selectedClient}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={options}
      getOptionLabel={(option) => option.name || ''}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      loading={loading}
      disabled={disabled}
      sx={sx}
      // 🔹 性能向上のため、オプションをフィルタリングしない
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          size={size}
          error={!!error}
          helperText={getHelperText()}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <div>
            <div style={{ fontWeight: 500 }}>
              {/* 🔹 検索文字をハイライト（オプション） */}
              {highlightMatch(option.name, inputValue)}
            </div>
            {option.address && (
              <div style={{ fontSize: '0.875rem', color: '#666' }}>
                {option.address}
              </div>
            )}
          </div>
        </li>
      )}
      noOptionsText={getNoOptionsText()}
      loadingText="検索中..."
    />
  );
};

// 🔹 検索文字のハイライト用ユーティリティ関数
const highlightMatch = (text, query) => {
  if (!query || query.length < 1) return text;
  
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return text;
  
  return (
    <>
      {text.substring(0, index)}
      <strong style={{ backgroundColor: '#fff3cd' }}>
        {text.substring(index, index + query.length)}
      </strong>
      {text.substring(index + query.length)}
    </>
  );
};

export default ClientAutocomplete;